<template>
  <div class="tender-query-view-mode clearfix">
    <div class="row">
      <div class="small-6 medium-6 large-6">
        <label class="emphasis">{{ $t('tender.query.closingTime') }}:</label>
        <date-time-selector
          :date="query.closingDate"
          :timeOptions="timeOptions"
          :disabled="true"
        />
      </div>
      <div
        class="small-3 medium-3 large-3"
        v-show="!isContractor && issuedTime"
      >
        <label>{{ $t('tender.query.issuedTime') }}:</label>
        <label class="submitted-time">{{ issuedTime }}</label>
      </div>
      <div v-show="submittedTime">
        <label>{{ $t('tender.query.submittedBy') }}:</label>
        <label class="submitted-time">{{ submitterName }} on {{ submittedTime }}</label>
      </div>
    </div>

    <question
      :readonly="true"
      :query="query"
    />

    <div class="query-attachments">
      <div v-if="engineerFiles.length">
        <label>{{ $t('tender.tenderAttachment') }}</label>
        <attachments
          :noHighlight="true"
          :editView="false"
          :files.sync="engineerFiles"
          :zipName="`${zipPrefix}_Attachments.zip`"
        />
      </div>
      <div v-if="vendorFiles.length">
        <label>{{ $t('vendor.attachment.vendor') }}</label>
        <attachments
          :noHighlight="true"
          :editView="false"
          :files.sync="vendorFiles"
          :zipName="`${zipPrefix}_Vendor_Attachments.zip`"
        />
      </div>
    </div>

    <div class="row action-btns align-right">
      <button
        v-if="withEditBtn"
        v-note="{text:noteText, enable: !editable}"
        :disabled="!editable"
        class="btn btn--darkblue"
        @click="edit()"
      >{{ $t('action.edit') }}</button>
      <button
        :disabled="!editable"
        class="btn btn--orange"
        v-if="isDraft && isPicView"
        @click="edit(true)"
      >{{ $t('action.issue') }} {{ $t('tender.query.name') }}</button>
    </div>
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapGetters } from 'vuex'

import Attachments from '@/modules/tenders/view/Attachments'
import DateTimeSelector from '@/controls/DateTimeSelector'
import note from '@/plugin/VNoteDirective'
import Question from '@/modules/tenders/queries/Question'
import queryMixin from './queryMixin'
import TextField from '@/controls/TextField'

import { checkQueryEditable } from '@/api/queries/request'
import { getAttachments } from '@/api/docs/request'
import { queryStatus } from '@/utils/locale'
import { timeOptions } from '@/utils/consts'
import * as xfs from '@/utils/xfs'
import dateUtil from '@/utils/dateUtil'

export default {
  name: 'tenderQueryView',
  mixins: [queryMixin],
  components: {
    DateTimeSelector,
    TextField,
    Attachments,
    Question,
  },

  props: {
    query: {
      type: Object,
      default: null,
    },
    index: {
      type: Number,
      default: 0,
    },
    bidId: {
      type: String,
      default: null,
    },
    withEditBtn: {
      type: Boolean,
      default: false,
    },
  },

  data () {
    return {
      timeOptions,
      vendorFiles: [],
      engineerFiles: [],
      editable: true,
      underEditBy: '',
    }
  },
  directives: { note },
  methods: {
    async downloadFile (fileId) {
      await xfs.downloadFile(`/docs/${fileId}`)
    },
    async loadFiles () {
      this.engineerFiles = await getAttachments(this.query.intResourceId)
      if (this.isSubmitted) {
        this.vendorFiles = await getAttachments(this.query.id)
      } else {
        this.vendorFiles = []
      }
    },
    validateQuery () {
      return this.query.closingDate && !this.query.questions.some(q => !lodash.get(q, 'question.length') > 0)
    },
    async edit (requestIssue) {
      await this.checkEditable(this.query.id)
      if (this.editable) {
        this.$emit('queryEditEvent', { requestIssue })
      }
    },
    async checkEditable (queryId) {
      if (!this.isPicView && !this.isRepresentative) return
      if (this.query.status !== queryStatus.DRAFT) {
        this.editable = false
        return
      }
      const result = await checkQueryEditable(queryId, this.currentEditAccount)
      this.editable = result ? result.editable : false
      this.underEditBy = result ? result.editorName : ''
    },
  },

  async mounted () {
    await this.loadFiles()
    if (this.withEditBtn) {
      await this.checkEditable(this.query.id)
    }
  },

  watch: {
    async query () {
      await this.loadFiles()
      await this.checkEditable(this.query.id)
    },
  },

  computed: {
    ...mapGetters('user', ['isContractor', 'currentUser']),
    ...mapGetters('tenders', ['isPicView', 'isRepresentative', 'currentEditAccount']),
    zipPrefix () {
      return `TQ${this.query.order}`
    },
    questions () {
      return this.query.questions || []
    },
    submittedTime () {
      return this.query.submittedDate ? dateUtil.formatDate(this.query.submittedDate, 'dd MMM YYYY HH:mm') : ''
    },
    submitterName () {
      return this.query.submitterName
    },
    issuedTime () {
      return this.query.issuedDate ? dateUtil.formatDate(this.query.issuedDate, 'dd MMM YYYY HH:mm') : ''
    },
    isSubmitted () {
      return this.query.status === queryStatus.SUBMITTED
    },
    isDraft () {
      return this.query.status === queryStatus.DRAFT
    },
    noteText () {
      return !this.editable ? this.$t('tender.underEdit', {editor: this.underEditBy}) : ''
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/tender/query';
.submitted-time {
  font-weight: normal;
}
.action-btns {
  float: right;
  margin-top: 20px;
  button {
    width: auto;
    padding: 8px 15px;
    vertical-align: middle;
    cursor: pointer;
  }
  button + button, .dialog-slot {
    margin-left: 24px;
  }
}
</style>
